import type { AlertLevel } from '../core/alert-rules';
import type { Speaker } from '../core/transcript';
import type { SummaryView } from '../shared/summary-view';

const LEVEL_TEXT: Record<AlertLevel, string> = {
  ok: 'Всё спокойно',
  attention: 'Обратите внимание',
  urgent: 'Срочно: сообщили вам сразу после звонка',
};

const SPEAKER_TEXT: Record<Speaker, string> = { vera: 'Вера', parent: 'Вы (мама)' };

/** Looks an element up and checks its type at runtime, so a markup change fails loudly. */
function byId<T extends HTMLElement>(id: string, type: new () => T): T {
  const element = document.getElementById(id);
  if (!(element instanceof type)) throw new Error(`#${id} is missing or not a ${type.name}`);
  return element;
}

const formatClock = (seconds: number): string =>
  `${String(Math.floor(seconds / 60))}:${String(seconds % 60).padStart(2, '0')}`;

/** All DOM access of the demo page. Text is always set via textContent, never as HTML. */
export class DemoView {
  readonly liveButton = byId('live', HTMLButtonElement);
  readonly sampleButton = byId('sample', HTMLButtonElement);
  readonly turnstileSlot = byId('turnstile', HTMLDivElement);
  private readonly notice = byId('notice', HTMLParagraphElement);
  private readonly dot = byId('dot', HTMLSpanElement);
  private readonly status = byId('status', HTMLSpanElement);
  private readonly timer = byId('timer', HTMLSpanElement);
  private readonly transcript = byId('transcript', HTMLOListElement);
  private readonly summaryPanel = byId('summary-panel', HTMLDivElement);
  private readonly summaryTitle = byId('summary-title', HTMLSpanElement);
  private readonly badge = byId('badge', HTMLSpanElement);
  private readonly headline = byId('headline', HTMLHeadingElement);
  private readonly facts = byId('facts', HTMLDListElement);
  private readonly quote = byId('quote', HTMLQuoteElement);

  setBusy(busy: boolean): void {
    this.liveButton.disabled = busy;
    this.sampleButton.disabled = busy;
  }

  showNotice(text: string | null): void {
    this.notice.hidden = text === null;
    this.notice.textContent = text ?? '';
  }

  startConversation(statusText: string): void {
    this.transcript.replaceChildren();
    this.summaryPanel.hidden = true;
    this.setStatus(statusText, true);
    this.setTimer(0);
  }

  setStatus(text: string, live: boolean): void {
    this.status.textContent = text;
    this.dot.className = live ? 'dot live' : 'dot';
  }

  setTimer(seconds: number): void {
    this.timer.textContent = formatClock(seconds);
  }

  addTurn(speaker: Speaker, text: string): void {
    const item = document.createElement('li');
    item.className = `line ${speaker}`;
    const label = document.createElement('b');
    label.textContent = SPEAKER_TEXT[speaker];
    item.append(label, text);
    this.transcript.append(item);
  }

  showSummary(view: SummaryView, title: string): void {
    this.summaryTitle.textContent = title;
    this.badge.className = `badge ${view.level}`;
    this.badge.textContent = LEVEL_TEXT[view.level];
    this.headline.textContent = view.headline;
    this.facts.replaceChildren(
      ...view.rows.flatMap(([term, value]) => {
        const dt = document.createElement('dt');
        dt.textContent = term;
        const dd = document.createElement('dd');
        dd.textContent = value;
        return [dt, dd];
      }),
    );
    this.quote.hidden = view.quote === null;
    this.quote.textContent = view.quote ? `«${view.quote}»` : '';
    this.summaryPanel.hidden = false;
  }
}
